/** A topic at 60% or more reads fine; below it, it is a weak point. */
export const TOPIC_PASS = 60;
/** The weak list stays short enough to act on. */
export const MAX_WEAK_TOPICS = 5;

export interface TopicAnswer {
  sectionId: string;
  sectionTitle: string;
  isCorrect: boolean;
  /** Items right out of items asked: a passage counts each sub-question. Absent means one item. */
  itemsCorrect?: number;
  itemsTotal?: number;
  /** Waiting for a human grader: `isCorrect` says nothing yet, so the answer is left out of topic scores. */
  gradingPending?: boolean;
}

export interface TopicScore {
  id: string;
  title: string;
  correct: number;
  total: number;
  percent: number;
}

/** Items right out of items asked across answers. A 40-question Reading passage is 40, not 1. */
export function tallyItems(answers: Array<Pick<TopicAnswer, 'isCorrect' | 'itemsCorrect' | 'itemsTotal'>>): {
  correct: number;
  total: number;
} {
  return answers.reduce(
    (sum, a) => ({
      correct: sum.correct + (a.itemsCorrect ?? (a.isCorrect ? 1 : 0)),
      total: sum.total + (a.itemsTotal ?? 1),
    }),
    { correct: 0, total: 0 },
  );
}

/** Score per topic (a section is a topic), in the order the sections arrive. Items, not question documents, are counted. */
export function topicBreakdown(answers: TopicAnswer[]): TopicScore[] {
  const byId = new Map<string, Omit<TopicScore, 'percent'>>();
  for (const a of answers) {
    if (a.gradingPending) continue;
    const id = a.sectionId || '__general__';
    const entry = byId.get(id) ?? { id, title: a.sectionTitle, correct: 0, total: 0 };
    const items = tallyItems([a]);
    entry.total += items.total;
    entry.correct += items.correct;
    byId.set(id, entry);
  }
  return Array.from(byId.values()).map((t) => ({
    ...t,
    percent: t.total > 0 ? Math.round((t.correct / t.total) * 100) : 0,
  }));
}

/**
 * The topics under the pass mark, weakest first. Untitled sections are not topics, and a result with one topic
 * has nothing to compare it against: its weak point would just be the whole score.
 */
export function weakTopics(topics: TopicScore[]): TopicScore[] {
  const titled = topics.filter((t) => t.title.trim() !== '' && t.total > 0);
  if (titled.length < 2) return [];
  return titled
    .filter((t) => t.percent < TOPIC_PASS)
    .sort((a, b) => a.percent - b.percent || b.total - b.correct - (a.total - a.correct))
    .slice(0, MAX_WEAK_TOPICS);
}

interface WeakTopicsVisibility {
  /** IELTS and SAT carry their own breakdown (skills, domains). */
  hasScaledReport: boolean;
  /** A candidate in the portal gets the study review, which lists weak topics itself when it has judged any. */
  isPortal: boolean;
  studyReviewLoading: boolean;
  studyReviewJudged: number;
}

/** Whether a result page shows the weak-topics card: never twice, and never while it is still unclear which one wins. */
export function shouldShowWeakTopics({ hasScaledReport, isPortal, studyReviewLoading, studyReviewJudged }: WeakTopicsVisibility): boolean {
  if (hasScaledReport) return false;
  if (!isPortal) return true;
  return !studyReviewLoading && studyReviewJudged === 0;
}
